import { SalesOrderFiltersState } from './types';

export interface EndpointFilters {
	[key: string]: string | number | boolean;
}

const formatDateForAPI = (date: Date | null): string | undefined => {
	if (!date) return undefined;
	// Usar formato local para evitar problemas de zona horaria
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');
	return `${year}-${month}-${day}`;
};

const getDateRangeFromPreset = (preset: string): { from: Date | null; to: Date | null } => {
	const today = new Date();
	today.setHours(0, 0, 0, 0);

	switch (preset) {
		case 'today':
			return { from: new Date(today), to: new Date(today) };

		case 'thisWeek': {
			const startOfWeek = new Date(today);
			startOfWeek.setDate(today.getDate() - today.getDay());
			const endOfWeek = new Date(startOfWeek);
			endOfWeek.setDate(startOfWeek.getDate() + 6);
			return { from: startOfWeek, to: endOfWeek };
		}

		case 'thisMonth': {
			const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
			const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
			return { from: startOfMonth, to: endOfMonth };
		}

		case 'last30Days': {
			const from = new Date(today);
			from.setDate(today.getDate() - 30);
			return { from, to: new Date(today) };
		}

		case 'lastMonth': {
			const startOfLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
			const endOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
			return { from: startOfLastMonth, to: endOfLastMonth };
		}

		case 'next10Days': {
			const to = new Date(today);
			to.setDate(today.getDate() + 10);
			return { from: new Date(today), to };
		}

		case 'next20Days': {
			const to = new Date(today);
			to.setDate(today.getDate() + 20);
			return { from: new Date(today), to };
		}

		case 'next30Days': {
			const to = new Date(today);
			to.setDate(today.getDate() + 30);
			return { from: new Date(today), to };
		}

		case 'next45Days': {
			const to = new Date(today);
			to.setDate(today.getDate() + 45);
			return { from: new Date(today), to };
		}

		default:
			return { from: null, to: null };
	}
};

export const convertSalesOrderFiltersToEndpoint = (
	filters: SalesOrderFiltersState,
	currentUserName: string,
): EndpointFilters => {
	const endpointFilters: EndpointFilters = {};

	// Filtros rápidos
	// Si hay un vendedor seleccionado, usar ese. Si no, y solo las mías está activo, usar el usuario actual
	if (filters.createdBy && filters.createdBy.trim() !== '') {
		endpointFilters.createdBy = filters.createdBy.trim();
	} else if (filters.onlyMine) {
		endpointFilters.createdBy = currentUserName;
	}

	if (filters.dealId && filters.dealId.trim() !== '') {
		const dealId = parseInt(filters.dealId);
		if (!isNaN(dealId)) {
			endpointFilters.dealId = dealId;
		}
	}

	if (filters.segmentType && filters.segmentType.trim() !== '') {
		endpointFilters.segmentType = filters.segmentType;
	}

	if (filters.invoicingStatuses && filters.invoicingStatuses.length > 0) {
		// La API espera un CSV en un único query param: invoicingStatuses=Pending,Partial
		endpointFilters.invoicingStatuses = filters.invoicingStatuses.join(',');
	}

	if (filters.hasPendingPayAmount !== null && filters.hasPendingPayAmount !== undefined) {
		endpointFilters.hasPendingPayAmount = filters.hasPendingPayAmount;
	}

	if (filters.customerEmpty !== null && filters.customerEmpty !== undefined) {
		// Si customerEmpty es true, buscar clientes vacíos (null o array vacío)
		// Esto dependerá de cómo la API maneje este caso
		if (filters.customerEmpty) {
			endpointFilters.customerEmpty = true;
		}
	}

	if (filters.mainPassengerCompleteName && filters.mainPassengerCompleteName.trim() !== '') {
		endpointFilters.mainPassengerCompleteName = filters.mainPassengerCompleteName.trim();
	}

	if (filters.customerCompleteName && filters.customerCompleteName.trim() !== '') {
		endpointFilters.customerCompleteName = filters.customerCompleteName.trim();
	}

	// Fechas - Creación (usar 'created' para fecha simple, o rangos si hay from/to)
	if (filters.createdDatePreset) {
		const dateRange = getDateRangeFromPreset(filters.createdDatePreset);
		if (dateRange.from && dateRange.to && dateRange.from.getTime() === dateRange.to.getTime()) {
			// Si from y to son iguales, usar fecha simple
			const formattedDate = formatDateForAPI(dateRange.from);
			if (formattedDate) {
				endpointFilters.created = formattedDate;
			}
		} else {
			if (dateRange.from) {
				const formattedDate = formatDateForAPI(dateRange.from);
				if (formattedDate) {
					endpointFilters.createdFrom = formattedDate;
				}
			}
			if (dateRange.to) {
				const formattedDate = formatDateForAPI(dateRange.to);
				if (formattedDate) {
					endpointFilters.createdTo = formattedDate;
				}
			}
		}
	} else {
		if (filters.createdDateFrom && filters.createdDateTo) {
			if (filters.createdDateFrom.getTime() === filters.createdDateTo.getTime()) {
				const formattedDate = formatDateForAPI(filters.createdDateFrom);
				if (formattedDate) {
					endpointFilters.created = formattedDate;
				}
			} else {
				const formattedFrom = formatDateForAPI(filters.createdDateFrom);
				const formattedTo = formatDateForAPI(filters.createdDateTo);
				if (formattedFrom) {
					endpointFilters.createdFrom = formattedFrom;
				}
				if (formattedTo) {
					endpointFilters.createdTo = formattedTo;
				}
			}
		} else if (filters.createdDateFrom) {
			const formattedDate = formatDateForAPI(filters.createdDateFrom);
			if (formattedDate) {
				endpointFilters.created = formattedDate;
			}
		} else if (filters.createdDateTo) {
			const formattedDate = formatDateForAPI(filters.createdDateTo);
			if (formattedDate) {
				endpointFilters.created = formattedDate;
			}
		}
	}

	// Fechas - Ida
	if (filters.travelDepartureDatePreset) {
		const dateRange = getDateRangeFromPreset(filters.travelDepartureDatePreset);
		if (dateRange.from) {
			const formattedDate = formatDateForAPI(dateRange.from);
			if (formattedDate) {
				endpointFilters.travelDepartureDateFrom = formattedDate;
			}
		}
		if (dateRange.to) {
			const formattedDate = formatDateForAPI(dateRange.to);
			if (formattedDate) {
				endpointFilters.travelDepartureDateTo = formattedDate;
			}
		}
	} else {
		if (filters.travelDepartureDateFrom) {
			const formattedDate = formatDateForAPI(filters.travelDepartureDateFrom);
			if (formattedDate) {
				endpointFilters.travelDepartureDateFrom = formattedDate;
			}
		}
		if (filters.travelDepartureDateTo) {
			const formattedDate = formatDateForAPI(filters.travelDepartureDateTo);
			if (formattedDate) {
				endpointFilters.travelDepartureDateTo = formattedDate;
			}
		}
	}

	// Fechas - Vuelta
	if (filters.travelReturnDatePreset) {
		const dateRange = getDateRangeFromPreset(filters.travelReturnDatePreset);
		if (dateRange.from) {
			const formattedDate = formatDateForAPI(dateRange.from);
			if (formattedDate) {
				endpointFilters.travelReturnDateFrom = formattedDate;
			}
		}
		if (dateRange.to) {
			const formattedDate = formatDateForAPI(dateRange.to);
			if (formattedDate) {
				endpointFilters.travelReturnDateTo = formattedDate;
			}
		}
	} else {
		if (filters.travelReturnDateFrom) {
			const formattedDate = formatDateForAPI(filters.travelReturnDateFrom);
			if (formattedDate) {
				endpointFilters.travelReturnDateFrom = formattedDate;
			}
		}
		if (filters.travelReturnDateTo) {
			const formattedDate = formatDateForAPI(filters.travelReturnDateTo);
			if (formattedDate) {
				endpointFilters.travelReturnDateTo = formattedDate;
			}
		}
	}

	// Fechas - Última edición
	if (filters.updatedDateFrom && filters.updatedDateTo) {
		if (filters.updatedDateFrom.getTime() === filters.updatedDateTo.getTime()) {
			const formattedDate = formatDateForAPI(filters.updatedDateFrom);
			if (formattedDate) {
				endpointFilters.updated = formattedDate;
			}
		} else {
			const formattedFrom = formatDateForAPI(filters.updatedDateFrom);
			const formattedTo = formatDateForAPI(filters.updatedDateTo);
			if (formattedFrom) {
				endpointFilters.updatedFrom = formattedFrom;
			}
			if (formattedTo) {
				endpointFilters.updatedTo = formattedTo;
			}
		}
	} else if (filters.updatedDateFrom) {
		const formattedDate = formatDateForAPI(filters.updatedDateFrom);
		if (formattedDate) {
			endpointFilters.updated = formattedDate;
		}
	} else if (filters.updatedDateTo) {
		const formattedDate = formatDateForAPI(filters.updatedDateTo);
		if (formattedDate) {
			endpointFilters.updated = formattedDate;
		}
	}

	// Montos
	if (filters.salesAmountFrom && filters.salesAmountFrom.trim() !== '') {
		endpointFilters.salesAmountFrom = parseFloat(filters.salesAmountFrom);
	}
	if (filters.salesAmountTo && filters.salesAmountTo.trim() !== '') {
		endpointFilters.salesAmountTo = parseFloat(filters.salesAmountTo);
	}
	if (filters.totalCostFrom && filters.totalCostFrom.trim() !== '') {
		endpointFilters.totalCostFrom = parseFloat(filters.totalCostFrom);
	}
	if (filters.totalCostTo && filters.totalCostTo.trim() !== '') {
		endpointFilters.totalCostTo = parseFloat(filters.totalCostTo);
	}
	if (filters.grossProfitFrom && filters.grossProfitFrom.trim() !== '') {
		endpointFilters.grossProfitFrom = parseFloat(filters.grossProfitFrom);
	}
	if (filters.grossProfitTo && filters.grossProfitTo.trim() !== '') {
		endpointFilters.grossProfitTo = parseFloat(filters.grossProfitTo);
	}
	if (filters.chargedAmountFrom && filters.chargedAmountFrom.trim() !== '') {
		endpointFilters.chargedAmountFrom = parseFloat(filters.chargedAmountFrom);
	}
	if (filters.chargedAmountTo && filters.chargedAmountTo.trim() !== '') {
		endpointFilters.chargedAmountTo = parseFloat(filters.chargedAmountTo);
	}
	if (filters.pendingPayAmountFrom && filters.pendingPayAmountFrom.trim() !== '') {
		endpointFilters.pendingPayAmountFrom = parseFloat(filters.pendingPayAmountFrom);
	}
	if (filters.pendingPayAmountTo && filters.pendingPayAmountTo.trim() !== '') {
		endpointFilters.pendingPayAmountTo = parseFloat(filters.pendingPayAmountTo);
	}

	// Identificadores
	if (filters.orderNumber && filters.orderNumber.trim() !== '') {
		const orderNumber = parseInt(filters.orderNumber);
		if (!isNaN(orderNumber)) {
			endpointFilters.orderNumber = orderNumber;
		}
	}
	if (filters.quotationId && filters.quotationId.trim() !== '') {
		const quotationId = parseInt(filters.quotationId);
		if (!isNaN(quotationId)) {
			endpointFilters.quotationId = quotationId;
		}
	}

	// Texto
	if (filters.destinations && filters.destinations.trim() !== '') {
		endpointFilters.destinations = filters.destinations.trim();
	}
	if (filters.clientReference && filters.clientReference.trim() !== '') {
		endpointFilters.clientReference = filters.clientReference.trim();
	}

	// Selects
	if (filters.servicesTypes && filters.servicesTypes.length > 0) {
		endpointFilters.servicesTypes = filters.servicesTypes.join(',');
	}
	if (filters.channelId && filters.channelId.trim() !== '') {
		const channelId = parseInt(filters.channelId);
		if (!isNaN(channelId)) {
			endpointFilters.channelId = channelId;
		}
	}
	// Booleanos
	if (filters.isBitrixDeal !== null && filters.isBitrixDeal !== undefined) {
		endpointFilters.isBitrixDeal = filters.isBitrixDeal;
	}
	if (filters.isReturnDateReached !== null && filters.isReturnDateReached !== undefined) {
		endpointFilters.isReturnDateReached = filters.isReturnDateReached;
	}

	return endpointFilters;
};
